import { difference, isEqual } from 'lodash-es';

import { type Conflict, type ReorderConflict } from '@editor2/types/conflict';
import { REORDER_ALLOW_LIST } from '@editor2/types/reorderAllowList';
import { ChangesStatus } from '@editor2/types/changesStatus';
import {
	getModifiedDeletedConflict,
	getPropertyConflict,
	getReorderConflict,
	getItemMoveConflict
} from './createConflictHelper';
import {
	getIncomingParent,
	getParentByAttributeName
} from '@editor2/services/conflictResolutionService';
import { getFamilyIds } from '@editor2/services/storeService';

const getValue = (value, obj, attr) => {
	if (!obj) return;
	return typeof value !== 'string' ? JSON.stringify(obj[attr]) : obj[attr];
};

const checkForModifiedDeletedConflict = (payload) => {
	const { attribute, initialValue, incomingValue, state } = payload;

	const conflicts = [];
	const conflictedItems = [];

	// if incoming deleted and current is modified
	const incomingDeletedOrMoved = difference(initialValue, incomingValue);
	const incomingDeleted = incomingDeletedOrMoved.filter(
		(id) => !getIncomingParent({ state, attribute, id })
	);

	incomingDeleted.map((id: string) => {
		const isModified =
			state.changes[attribute][id]?.includes(ChangesStatus.Modified) ||
			state.changes[attribute][id]?.includes(ChangesStatus.ChildrenReorder);

		if (isModified) {
			const entityType = state.initialCourse[attribute].entities[id].type;
			const data = { attribute, id, entityId: id, entityType, state };

			const conflict = getModifiedDeletedConflict(data);

			conflicts.push(conflict);
			conflictedItems.push(id);
		}
	});

	return { conflicts, conflictedItems };
};

const checkForItemMoveConflicts = (payload) => {
	const { attribute, initial, incoming, state } = payload;
	const initialValue = initial && initial[attribute];
	const incomingValue = incoming[attribute];

	const newOrMovedFromIncomingItems = difference(incomingValue, initialValue);

	// moved from items in incoming
	const movedFromItems = newOrMovedFromIncomingItems.filter((itemId: string) => {
		const initialParent = getParentByAttributeName({
			state: state.tempInitCourse,
			attribute,
			entityId: itemId
		});
		return !!initialParent;
	});

	// create item-move conflict if the item is moved to another parent in current changes too
	const moveItemConflicts = [];

	movedFromItems.forEach((itemId: string) => {
		const initialParent = getParentByAttributeName({
			state: state.tempInitCourse,
			attribute,
			entityId: itemId
		});
		const currentParent = getParentByAttributeName({
			state: state.tempCurrentCourse,
			attribute,
			entityId: itemId
		});

		// check if item is moved to another parent in current changes
		if (initialParent.family_id !== currentParent.family_id) {
			const incomingParent = getIncomingParent({ attribute, id: itemId, state });

			const conflict = getItemMoveConflict({
				initial: initialParent,
				current: currentParent,
				incoming: { ...incomingParent, [attribute]: getFamilyIds(incomingParent[attribute]) },
				attribute,
				entityId: itemId,
				entityType: state[attribute].entities[itemId].type
			});

			moveItemConflicts.push(conflict);
		}
	});

	return moveItemConflicts;
};

const getDuplicateItems = (payload) => {
	const { attribute, initial, incoming, state } = payload;
	const initialValue = initial && initial[attribute];
	const incomingValue = incoming[attribute];

	const newIncomingItems = difference(incomingValue, initialValue);

	const duplicateItems = newIncomingItems.filter((itemId: string) => {
		const itemCurrentParent = getParentByAttributeName({
			state: state.tempCurrentCourse,
			attribute,
			entityId: itemId
		});

		// search for currentParent equivalent in incoming changes
		const currentParentIncomingEquivalent =
			itemCurrentParent &&
			state.diffNodes.find((node) => node.family_id === itemCurrentParent.family_id);

		// if it's not present (which mean no changes) or it DOES include the itemId - then it's a duplicate
		// otherwise it's not a duplication (changes aren't just applied yet)
		const isDuplicate =
			!currentParentIncomingEquivalent ||
			currentParentIncomingEquivalent[attribute].includes(itemId);

		return isDuplicate
			? itemCurrentParent && itemCurrentParent.family_id !== initial.family_id
			: false;
	});

	return duplicateItems;
};

const checkForReorderConflict = (payload) => {
	const { attribute, initial, current, incoming, state } = payload;

	let conflicts: (Conflict | ReorderConflict)[] = [];
	let updatedInitial = initial && initial[attribute];
	let updatedCurrent = current && current[attribute];

	const initialValue = initial && initial[attribute];
	const currentValue = current && current[attribute];
	const incomingValue = incoming[attribute];

	switch (true) {
		case isEqual(currentValue, incomingValue): {
			updatedInitial = [...incomingValue];
			updatedCurrent = [...incomingValue];
			break;
		}

		case isEqual(initialValue, currentValue) && !isEqual(initialValue, incomingValue): {
			const payloadData = { attribute, initialValue, incomingValue, state };

			// check for othe conflicts
			const { conflicts: mdConflicts, conflictedItems } =
				checkForModifiedDeletedConflict(payloadData);
			const moveItemConflicts = checkForItemMoveConflicts(payload);

			// check for duplications if there are 'moved from' items
			const duplicateItems = getDuplicateItems(payload);
			const filteredItems = incomingValue.filter((item) => !duplicateItems.includes(item));

			// no conflict, just apply the changes both to initial and current
			updatedInitial = [...incomingValue, ...conflictedItems];
			updatedCurrent = [...filteredItems, ...conflictedItems]; // do NOT remove conflicted

			conflicts = [...conflicts, ...mdConflicts, ...moveItemConflicts];
			break;
		}

		case !isEqual(initialValue, currentValue) && !isEqual(initialValue, incomingValue): {
			const isEqualLength =
				initialValue.length === currentValue.length && initialValue.length === incomingValue.length;
			const positionConflictPresent = initialValue.some(
				(id, i) => initialValue[i] !== currentValue[i] && initialValue[i] !== incomingValue[i]
			);

			if (positionConflictPresent || !isEqualLength) {
				const conflict = getReorderConflict({ initial, current, incoming, attribute });
				conflicts = [...conflicts, conflict];
			} else {
				const merged = initialValue.map((id, i) => {
					if (initialValue[i] === currentValue[i]) return incomingValue[i];
					else if (initialValue[i] === incomingValue[i]) return currentValue[i];
				});

				updatedInitial = [...incomingValue];
				updatedCurrent = [...merged];
			}
			break;
		}

		default:
			break;
	}

	return { conflicts, updatedInitial, updatedCurrent };
};

const checkForPropertyConflict = (payload) => {
	const { attributes, attribute, initial, current, incoming, state } = payload;

	const conflicts: (Conflict | ReorderConflict)[] = [];
	let updatedInitial = initial && initial[attribute];
	let updatedCurrent = current && current[attribute];

	const initialValue = getValue(attributes[attribute], initial, attribute);
	const currentValue = getValue(attributes[attribute], current, attribute);
	const incomingValue = getValue(attributes[attribute], incoming, attribute);

	switch (true) {
		case isEqual(currentValue, incomingValue): {
			updatedInitial = incoming[attribute];
			updatedCurrent = incoming[attribute];
			break;
		}

		case initialValue === currentValue && initialValue !== incomingValue:
			// no conflict, just apply the changes both to initial and current
			updatedInitial = incoming[attribute];
			updatedCurrent = incoming[attribute];
			break;

		case initialValue !== currentValue && initialValue !== incomingValue: {
			const conflict = getPropertyConflict({ initial, current, incoming, attribute });
			conflicts.push(conflict);
			break;
		}

		default:
			break;
	}

	return { conflicts, updatedInitial, updatedCurrent };
};

export const getUpdatedValuesAndConflicts = (payload) => {
	const { attributes, initial, current } = payload;

	let conflicts: (Conflict | ReorderConflict)[] = [];
	const updatedInitial = { ...initial };
	const updatedCurrent = { ...current };

	Object.keys(attributes).map((attribute) => {
		const isReorderProperty = REORDER_ALLOW_LIST.includes(attribute);

		const res = isReorderProperty
			? checkForReorderConflict({ ...payload, attribute })
			: checkForPropertyConflict({ ...payload, attribute });

		updatedInitial[attribute] = res.updatedInitial;
		updatedCurrent[attribute] = res.updatedCurrent;

		conflicts = [...conflicts, ...res.conflicts];
	});

	return { conflicts, updatedInitial, updatedCurrent };
};
